import { type FC, type KeyboardEvent, useEffect, useRef, type MutableRefObject } from 'react';
import classNames from 'classnames/bind';
import { optionalProp } from '../core';
import { Theme } from '../types/enums';
import { FAIcon, type IconTheme } from '../Icons/Icons';
import GenericLink from '../GenericLink/GenericLink';
import { filterIconTheme } from '../utils/ThemeFilter/ThemeFilter';
import { type RawLinkPropsTypes } from './types/propsType';

import { TextLinkSize, TextLinkUnderlineStyle } from './types/enums';
import styles from './TextLink.module.scss';

const cx = classNames.bind(styles);
/**
 *
 * @description RawTextLink provide <a></a> with basic css;
 *  it can be used only through other Components.
 *  styling can be override through prop (className) only.
 *  Ex-  TextLink, Card with Images.
 *  Note :-  TextLink uses GenericLink.
 */

const RawTextLink: FC<RawLinkPropsTypes> = ({
  text,
  className,
  disabled,
  href = '',
  theme = Theme.BLACK,
  leftIcon,
  rightIcon,
  underline = TextLinkUnderlineStyle.HOVER,
  onClick,
  forwardedRef,
  ariaLabel,
  iconPosition, // extract away from rest props to avoid getting attached to raw DOM element
  onMouseEnter,
  onTouchStart,
  size = TextLinkSize.DEFAULT,
  ...restProps
}) => {
  const anchorRef = useRef() as MutableRefObject<HTMLSpanElement>;
  //filters underline class corresponding to prop
  const { NOHOVER, FIXED } = TextLinkUnderlineStyle;
  const CClassname = cx(
    'text-link',
    {
      'no-hover-underline': underline === NOHOVER,
      'fixed-underline': underline === FIXED,
    },
    [theme],
    className,
    { disabled: disabled },
    { 'small-size-link-text': size === TextLinkSize.SM },
  );
  const IconClassname = cx('icon', { 'small-size-link-icon': size === TextLinkSize.SM });

  const onClickWrapper: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    if (onClick) {
      e.preventDefault();
      onClick(e);
    }
  };
  useEffect(() => {
    if (onClick && anchorRef.current) {
      const anchorParentElement = anchorRef.current;
      const anchorElement = anchorParentElement.getElementsByTagName('a');
      anchorElement[0]?.addEventListener('click', (e: MouseEvent) => {
        e.preventDefault();
      });
    }
  }, [onClick, anchorRef.current]);
  return (
    <span
      role="link"
      aria-label={ariaLabel}
      className={CClassname}
      data-testid="textlink-test-id"
      onClick={onClickWrapper}
      onKeyDown={(e: KeyboardEvent<HTMLAnchorElement>) => {
        if (e.key === 'Enter') {
          restProps.onKeyDown?.(e);
        }
      }}
      tabIndex={0}
      ref={anchorRef}
    >
      {leftIcon && (
        <FAIcon
          {...leftIcon}
          theme={filterIconTheme(theme) as IconTheme}
          className={IconClassname}
          data-testid="textlink-left-icon-testid"
        />
      )}
      <GenericLink
        href={href}
        {...restProps}
        {...optionalProp('onTouchStart', onTouchStart)}
        {...optionalProp('onMouseEnter', onMouseEnter)}
        {...optionalProp('forwardedRef', forwardedRef)}
      >
        {text}
      </GenericLink>
      {rightIcon && (
        <FAIcon
          {...rightIcon}
          theme={filterIconTheme(theme) as IconTheme}
          className={IconClassname}
          data-testid="textlink-right-icon-testid"
        />
      )}
    </span>
  );
};
RawTextLink.displayName = 'RawTextLink';
export default RawTextLink;
